import { useCallback, useEffect, useState } from 'react'
import { LinkButton } from '@/atoms/Button'
import { MasksIcon } from '@/atoms/Icons/MasksIcon'
import { AsSpan, LabelSM, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants'
import { DecodedAccessToken, getJwtFromCookie, useLogoutUrl } from '@/utils/auth-utils'
import { useCountDown } from '@/utils/countdown/useCountdownHook'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

export const MasqueradeBanner: React.FC = () => {
  const { asPath, push } = useSafeRouter()
  const { logoutUrl, trackLogoutStarted } = useLogoutUrl(paths.index)
  const [decodedToken, setDecodedToken] = useState<DecodedAccessToken | null>(null)

  const countdown = useCountDown({
    skip: !decodedToken,
    endDate: decodedToken?.exp ? decodedToken.exp * 1000 : undefined,
    numUnits: 5,
  })

  const handleLogoutClicked = useCallback(() => {
    trackLogoutStarted('MasqueradeBanner')
    setDecodedToken(null)
  }, [trackLogoutStarted])

  // Refresh the masquerade token when the page changes
  useEffect(() => {
    const token = getJwtFromCookie()
    if (token && token.is_masquerade) setDecodedToken(token)
  }, [asPath])

  useEffect(() => {
    if (decodedToken && countdown && countdown.isExpired) {
      handleLogoutClicked()
      push(logoutUrl)
    }
  }, [decodedToken, countdown, handleLogoutClicked, push, logoutUrl])

  const userEmail = decodedToken?.target_email
  const formattedCountdown = countdown?.countdown?.toFormat('mm:ss')

  if (!decodedToken) return null

  return (
    <div className="fixed left-0 top-24 z-[10000001] h-0 w-full bg-transparent px-6">
      <div className="mx-auto flex h-fit w-fit flex-row items-center justify-center gap-2 rounded-lg bg-[#E40505] px-3 py-2 align-middle text-black md:gap-4 md:px-4">
        <div className="hidden shrink-0 md:block">
          <MasksIcon />
        </div>
        <div className="flex flex-col items-center justify-between gap-1 text-center align-middle md:flex-row">
          <div>
            <TitleSM as={AsSpan}>
              You’re masquerading as <span className="font-semibold">{userEmail}</span>.
            </TitleSM>
          </div>
          <div className="shrink-0 text-left md:w-[145px]">
            <TitleSM as={AsSpan}>Expires in {formattedCountdown}</TitleSM>
          </div>
        </div>
        <div className="shrink-0">
          <LinkButton variant="white" href={logoutUrl} onClick={handleLogoutClicked}>
            <LabelSM weight="semibold">End</LabelSM>
          </LinkButton>
        </div>
      </div>
    </div>
  )
}
